// Copyright (c) 2020 WiseTime. All rights reserved.

import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { RootState } from "app/store"
import { DialogStatus } from "features/shared/Dialog"
import * as Actions from "./actions"
import * as UserContextSlice from "../user-context/slice"
import { naturalOrderCollator } from "../../lib/utils"
import { TagCategory } from "../../generated/legebill/type/tag_category_pb"
import { Tag, TagKeyword, TagShallow } from "generated/legebill/type/tag_pb"
import {
  FavouriteTag,
  ListFavouriteTagsResponse,
  ListRecentTagsResponse,
  SearchTagsRequest,
  SearchTagsResponse,
  SearchTagsResponseShallow,
} from "generated/legebill/site/tag/v4/tag_pb"

export type TagId = string

export type TagsCache = {
  [id: string]: Tag.AsObject,
}

export type TagCategoriesCache = {
  [tagCategoryId: string]: TagCategory.AsObject,
}

export type SearchTagsResult = {
  searchString: string,
  response: SearchTagsResponse.AsObject,
}

export type SearchTagsResultShallow = {
  searchString: string,
  response: SearchTagsResponseShallow.AsObject,
}

export enum RecentTagsOrderBy {
  RELEVANCE = 1,
  NAME = 2,
}

export enum ViewMode {
  SEARCH = 1,
  BROWSE = 2,
}

export type TagsState = {
  recentTags: Tag.AsObject[],
  favouriteTags: FavouriteTag.AsObject[],
  favouriteTagsInitiallyLoaded: boolean,
  // Searched tags result
  resultTags: Tag.AsObject[],
  resultTagsShallow: TagShallow.AsObject[],
  isSearching: boolean,
  searchString: string,
  searchOrderBy: SearchTagsRequest.OrderBy,
  recentOrderBy: RecentTagsOrderBy,
  viewMode: ViewMode,
  tagsCache: TagsCache,
  tagCategoriesCache: TagCategoriesCache,
  dialogStatus: DialogStatus,
  isLoadingTag: boolean,
  isInitialBatchLoaded: boolean,
}

export const initialState: TagsState = {
  recentTags: [],
  favouriteTags: [],
  favouriteTagsInitiallyLoaded: false,
  resultTags: [],
  resultTagsShallow: [],
  isSearching: false,
  searchString: "",
  searchOrderBy: SearchTagsRequest.OrderBy.RELEVANCE,
  recentOrderBy: RecentTagsOrderBy.RELEVANCE,
  viewMode: ViewMode.SEARCH,
  tagsCache: {},
  tagCategoriesCache: {},
  dialogStatus: "CLOSED",
  isLoadingTag: false,
  isInitialBatchLoaded: false,
}

export type UpdateTagResult = {
  tagId: string,
  name: string,
  description: string,
  suggestionConfig: Tag.SuggestionConfig,
  defaultActivityTypeId: string,
}

export const slice = createSlice({
  name: "tags",
  initialState,
  reducers: {
    setRecentTags: (state, action: PayloadAction<ListRecentTagsResponse.AsObject>): void => {
      state.recentTags = action.payload.recentTagsList
      action.payload.recentTagsList.forEach((recentTag: Tag.AsObject) => {
        state.tagsCache[recentTag.id] = recentTag
      })
    },
    setResultTags: (state, action: PayloadAction<SearchTagsResult>): void => {
      if (action.payload.searchString === state.searchString) {
        state.resultTags = action.payload.response.tagsList
        state.isSearching = initialState.isSearching
        state.searchString = initialState.searchString
        action.payload.response.tagsList.forEach((resultTag: Tag.AsObject) => {
          state.tagsCache[resultTag.id] = resultTag
        })
      }
    },
    setResultTagsShallow: (state, action: PayloadAction<SearchTagsResultShallow>): void => {
      if (action.payload.searchString === state.searchString) {
        state.resultTagsShallow = action.payload.response.tagsShallowList
        state.isSearching = initialState.isSearching
        state.searchString = initialState.searchString
      }
    },
    resetResultTagsShallow: (state): void => {
      state.resultTagsShallow = initialState.resultTagsShallow
      state.isSearching = initialState.isSearching
    },
    resetResultTags: (state): void => {
      state.resultTags = initialState.resultTags
      state.isSearching = initialState.isSearching
    },
    setFavouriteTags: (state, action: PayloadAction<ListFavouriteTagsResponse.AsObject>): void => {
      state.favouriteTags = action.payload.favouriteTagsList
      action.payload.favouriteTagsList.forEach((favouriteTag: FavouriteTag.AsObject) => {
        const { tag } = favouriteTag
        if (tag) {
          state.tagsCache[tag.id] = tag
        }
      })
      state.favouriteTagsInitiallyLoaded = true
    },
    createFavouriteTagFulfilled: (state, action: PayloadAction<FavouriteTag.AsObject>): void => {
      state.favouriteTags.unshift(action.payload)
    },
    setTags: (state, action: PayloadAction<Tag.AsObject[]>): void => {
      action.payload.forEach((tag: Tag.AsObject) => {
        state.tagsCache[tag.id] = tag
      })
      state.isInitialBatchLoaded = true
    },
    setTagCategories: (state, action: PayloadAction<TagCategory.AsObject[]>): void => {
      action.payload.forEach(tagCategory => {
        state.tagCategoriesCache[tagCategory.id] = tagCategory
      })
    },
    updateTag: (state, action: PayloadAction<UpdateTagResult>): void => {
      const { tagId, name, description, suggestionConfig, defaultActivityTypeId } = action.payload
      const tag = state.tagsCache[tagId]
      if (tag) {
        tag.name = name
        tag.description = description
        tag.suggestionConfig = suggestionConfig
        tag.defaultActivityTypeId = defaultActivityTypeId
      }
    },
    setTag: (state, action: PayloadAction<Tag.AsObject>): void => {
      state.tagsCache[action.payload.id] = action.payload
      state.isLoadingTag = false
    },
    setIsLoadingTag: (state, action: PayloadAction<boolean>): void => {
      state.isLoadingTag = action.payload
    },
    createTagKeywordFulfilled: (state, action: PayloadAction<TagKeyword.AsObject>): void => {
      state.tagsCache[action.payload.tagId]?.keywordsList.push(action.payload)
    },
    deleteTagKeywordFulfilled: (state, action: PayloadAction<TagKeyword.AsObject>): void => {
      const cachedTag = state.tagsCache[action.payload.tagId]
      if (cachedTag !== undefined) {
        cachedTag.keywordsList = cachedTag.keywordsList.filter(keyword => action.payload.text !== keyword.text)
      }
    },
    setSearchOrderBy: (state, action: PayloadAction<SearchTagsRequest.OrderBy>): void => {
      state.searchOrderBy = action.payload
    },
    setRecentOrderBy: (state, action: PayloadAction<RecentTagsOrderBy>): void => {
      state.recentOrderBy = action.payload
    },
    setViewMode: (state, action: PayloadAction<ViewMode>): void => {
      state.viewMode = action.payload
    },
    setDialogStatus: (state, action: PayloadAction<DialogStatus>): void => {
      state.dialogStatus = action.payload
    },
    clearRecentAndFavouriteTags: (state): void => {
      state.recentTags = []
      state.favouriteTags = []
    },
    removeFromCache: (state, action: PayloadAction<TagId>): void => {
      delete state.tagsCache[action.payload]
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(Actions.searchTags, (state, action) => {
        state.isSearching = true
        state.searchString = action.payload.getSearchText()
      })
      .addCase(Actions.getTag, (state) => {
        state.isLoadingTag = true
      })
      .addCase(Actions.searchTagsShallow, (state, action) => {
        state.isSearching = true
        state.searchString = action.payload.getSearchText()
      })
      .addCase(UserContextSlice.setCurrentSiteId, (state) => {
        state.resultTagsShallow = initialState.resultTagsShallow
        state.favouriteTagsInitiallyLoaded = initialState.favouriteTagsInitiallyLoaded
      })
  },
})

export const {
  clearRecentAndFavouriteTags,
  createFavouriteTagFulfilled,
  createTagKeywordFulfilled,
  deleteTagKeywordFulfilled,
  removeFromCache,
  resetResultTags,
  resetResultTagsShallow,
  setDialogStatus,
  setFavouriteTags,
  setRecentTags,
  setResultTags,
  setResultTagsShallow,
  setTags,
  setTagCategories,
  setTag,
  setSearchOrderBy,
  setRecentOrderBy,
  setViewMode,
  setIsLoadingTag,
  updateTag,
} = slice.actions

// Use createSelector to generate memoized selectors
// https://redux.js.org/usage/deriving-data-selectors#createselector-overview
export const selectRecentTags = createSelector(
  (state: RootState): Tag.AsObject[] => state.tags.recentTags,
  (state: RootState): RecentTagsOrderBy => state.tags.recentOrderBy,
  (recentTags, recentOrderBy) => {
    if (recentOrderBy === RecentTagsOrderBy.NAME) {
      return recentTags.slice().sort((a, b) => a.name.localeCompare(b.name))
    }
    return recentTags
  }
)
export const selectResultTags = (state: RootState): Tag.AsObject[] => state.tags.resultTags
export const selectResultTagsShallow = (state: RootState): TagShallow.AsObject[] => state.tags.resultTagsShallow
export const selectIsSearching = (state: RootState): boolean => state.tags.isSearching
export const selectFavouriteTags = (state: RootState): FavouriteTag.AsObject[] => state.tags.favouriteTags
export const selectFavouriteTagsInitiallyLoaded = (state: RootState): boolean =>
  state.tags.favouriteTagsInitiallyLoaded
export const selectTagsCache = (state: RootState): TagsCache => state.tags.tagsCache
export const selectTagCategoriesCache = (state: RootState): TagCategoriesCache => state.tags.tagCategoriesCache
export const selectTagCategoryById = () => createSelector(
  (_: RootState, tagCategoryId: string) => tagCategoryId,
  selectTagCategoriesCache,
  (tagCategoryId, tagCategoriesCache): TagCategory.AsObject | undefined => tagCategoriesCache[tagCategoryId]
)
export const selectFavouriteTagId = () => createSelector(
  (_: RootState, tagId: string) => tagId,
  selectFavouriteTags,
  (tagId, favouriteTags): string | undefined =>
    favouriteTags.find(t => t.tag?.id === tagId)?.id,
)
export const selectTagsByCategoryId = () => createSelector(
  (_: RootState, tagCategoryId: string) => tagCategoryId,
  selectTagCategoriesCache,
  selectTagsCache,
  (tagCategoryId, tagCategoriesCache, tagsCache): Tag.AsObject[] | undefined => {
    if (tagCategoriesCache[tagCategoryId] === undefined) {
      return undefined
    }
    return Object.values(tagsCache)
      .filter(t => t.tagCategoryId === tagCategoryId)
      .sort((t1, t2) => naturalOrderCollator.compare(t1.name, t2.name))
  }
)
export const selectSearchOrderBy = (state: RootState): SearchTagsRequest.OrderBy => state.tags.searchOrderBy
export const selectRecentOrderBy = (state: RootState): RecentTagsOrderBy => state.tags.recentOrderBy
export const selectViewMode = (state: RootState): ViewMode => state.tags.viewMode
export const selectDialogStatus = (state: RootState): DialogStatus => state.tags.dialogStatus
export const selectIsLoadingTag = (state: RootState): boolean => state.tags.isLoadingTag
export const selectIsInitialBatchLoaded = (state: RootState): boolean => state.tags.isInitialBatchLoaded

export const selectTag = () => createSelector(
  (_: RootState, tagId: TagId) => tagId,
  (state: RootState): TagsCache => state.tags.tagsCache,
  (tagId: string, tagsCache: TagsCache): Tag.AsObject | undefined => tagsCache[tagId],
)

export default slice.reducer
